import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, ExcelIcon, exportToExcel,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  C360Table, ColumnChooser,
  ViewIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { NotificationComposeTab, NotificationSentTab } from './NotificationBroadcastTabs';

const API_BASE = '/api/notifications/admin';

const TYPE_META = {
  ORDER_STATUS: { label: 'Sipariş', tone: 'info' },
  CAMPAIGN: { label: 'Kampanya', tone: 'info' },
  TASK_ASSIGNED: { label: 'Görev', tone: 'wait' },
  POINT_OPENING: { label: 'Nokta Açma', tone: 'ok' },
  TRAINING: { label: 'Eğitim', tone: 'ok' },
  SYSTEM: { label: 'Sistem', tone: 'neutral' },
};

const TYPE_OPTIONS = [
  { value: 'ORDER_STATUS', label: 'Sipariş' },
  { value: 'CAMPAIGN', label: 'Kampanya' },
  { value: 'TASK_ASSIGNED', label: 'Görev' },
  { value: 'POINT_OPENING', label: 'Nokta Açma' },
  { value: 'TRAINING', label: 'Eğitim' },
  { value: 'SYSTEM', label: 'Sistem' },
];

const READ_OPTIONS = [
  { value: 'false', label: 'Okunmamış' },
  { value: 'true', label: 'Okunmuş' },
];

// c360 kolon filtre meta'sı. Bu ekranın veri kaynağı OData değil özel REST
// (/api/notifications/admin) olduğundan filtreleme istemci tarafında uygulanır;
// odataField yalnızca alan adı eşlemesi için tutulur.
const COLUMN_FILTER_META = {
  userName: { type: 'text', odataField: 'userName' },
  type: { type: 'select', odataField: 'type', options: TYPE_OPTIONS },
  title: { type: 'text', odataField: 'title' },
  isRead: { type: 'select', odataField: 'isRead', valueFormat: 'raw', options: READ_OPTIONS },
};

const SEARCH_FIELDS = ['userName', 'userEmail', 'title', 'body', 'pointName'];
const PAGE_SIZE_DEFAULT = 10;

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapResponse(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return {
    items: pick(data, 'items', 'Items') ?? [],
    total: pick(data, 'total', 'Total') ?? 0,
    unreadCount: pick(data, 'unreadCount', 'UnreadCount') ?? 0,
    readCount: pick(data, 'readCount', 'ReadCount') ?? 0,
  };
}

function typeLabel(type) {
  return (TYPE_META[type] || { label: type || '-' }).label;
}

function typeBadge(type) {
  const meta = TYPE_META[type] || { label: type || '-', tone: 'neutral' };
  return <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>;
}

function readBadge(isRead) {
  return isRead
    ? <StatusBadge tone="neutral">Okundu</StatusBadge>
    : <StatusBadge tone="info">Yeni</StatusBadge>;
}

function formatDate(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return '-'; }
}

// Tekil kolon filtresini istemci tarafında uygular (OData semantiğini taklit eder).
function matchColumnFilter(meta, filter, rawValue) {
  if (!meta || !filter) return true;
  const v = filter.value;
  if (v === undefined || v === null || v === '') return true;
  if (meta.type === 'text') {
    const cell = (rawValue == null ? '' : String(rawValue)).toLocaleLowerCase('tr-TR');
    const needle = String(v).toLocaleLowerCase('tr-TR');
    const op = filter.op || 'contains';
    if (op === 'eq') return cell === needle;
    if (op === 'startswith') return cell.startsWith(needle);
    return cell.includes(needle);
  }
  if (meta.type === 'select') {
    if (meta.valueFormat === 'raw') return Boolean(rawValue) === (String(v) === 'true');
    return String(rawValue ?? '') === String(v);
  }
  if (meta.type === 'number') {
    const num = Number(v);
    if (Number.isNaN(num)) return true;
    const cell = Number(rawValue);
    const op = filter.op || 'eq';
    if (op === 'gt') return cell > num;
    if (op === 'lt') return cell < num;
    if (op === 'ge') return cell >= num;
    if (op === 'le') return cell <= num;
    return cell === num;
  }
  return true;
}

function columnRawValue(columnId, row) {
  switch (columnId) {
    case 'userName': return pick(row, 'userName', 'UserName');
    case 'type': return pick(row, 'type', 'Type');
    case 'title': return pick(row, 'title', 'Title');
    case 'isRead': return pick(row, 'isRead', 'IsRead');
    default: return undefined;
  }
}

function NotificationMonitorTab() {
  const [allRows, setAllRows] = useState([]);
  const [counts, setCounts] = useState({ total: 0, unreadCount: 0, readCount: 0 });
  const [loading, setLoading] = useState(true); // ilk paint'te boş-durum yanıp sönmesin
  const [toast, setToast] = useState(null);

  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [sortField, setSortField] = useState('CreatedDate');
  const [sortDir, setSortDir] = useState('desc');
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [currentPage, setCurrentPage] = useState(1);
  const [columnVisibility, setColumnVisibility] = useState({});

  const [selected, setSelected] = useState(null);
  const [detailRow, setDetailRow] = useState(null);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3500);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('take', '500');
      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const data = unwrapResponse(payload);
      setAllRows(Array.isArray(data.items) ? data.items : []);
      setCounts({ total: data.total, unreadCount: data.unreadCount, readCount: data.readCount });
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const applyFilterText = useCallback((text) => { setFilterText(text); setCurrentPage(1); }, []);
  const setColumnFilter = useCallback((columnId, next) => {
    setColumnFilters((prev) => ({ ...prev, [columnId]: next }));
    setCurrentPage(1);
  }, []);
  const clearColumnFilter = useCallback((columnId) => {
    setColumnFilters((prev) => { const copy = { ...prev }; delete copy[columnId]; return copy; });
    setCurrentPage(1);
  }, []);
  const applySort = useCallback((field, dir) => { setSortField(field || 'CreatedDate'); setSortDir(dir || 'asc'); setCurrentPage(1); }, []);

  const filteredSorted = useMemo(() => {
    const needle = filterText.trim().toLocaleLowerCase('tr-TR');
    const matchSearch = (row) => {
      if (!needle) return true;
      return SEARCH_FIELDS.some((f) => {
        const v = pick(row, f, f.charAt(0).toUpperCase() + f.slice(1));
        return v && String(v).toLocaleLowerCase('tr-TR').includes(needle);
      });
    };
    const matchColumns = (row) => Object.entries(columnFilters).every(([colId, filter]) =>
      matchColumnFilter(COLUMN_FILTER_META[colId], filter, columnRawValue(colId, row)));

    const filtered = allRows.filter((r) => matchSearch(r) && matchColumns(r));
    const dir = sortDir === 'desc' ? -1 : 1;
    const sortKey = (row) => {
      switch (sortField) {
        case 'UserName': return pick(row, 'userName', 'UserName') || '';
        case 'Type': return pick(row, 'type', 'Type') || '';
        case 'Title': return pick(row, 'title', 'Title') || '';
        case 'IsRead': return pick(row, 'isRead', 'IsRead') ? 1 : 0;
        case 'CreatedDate':
        default: {
          const v = pick(row, 'createdDate', 'CreatedDate');
          return v ? new Date(v).getTime() : 0;
        }
      }
    };
    return [...filtered].sort((a, b) => {
      const av = sortKey(a);
      const bv = sortKey(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [allRows, filterText, columnFilters, sortField, sortDir]);

  const totalCount = filteredSorted.length;
  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, currentPage, pageSize]);

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('createdDate', {
      header: 'Tarih', enableColumnFilter: false,
      cell: (info) => formatDate(info.getValue()),
    }),
    columnHelper.accessor('userName', {
      header: 'Kullanıcı', meta: { filterType: 'text' },
      cell: ({ row }) => {
        const name = pick(row.original, 'userName', 'UserName');
        const email = pick(row.original, 'userEmail', 'UserEmail');
        const userId = pick(row.original, 'userId', 'UserId');
        return (
          <div>
            <div style={{ fontWeight: 600 }}>{name || `#${userId}`}</div>
            {email && <div style={{ fontSize: 11, color: '#6b7280' }}>{email}</div>}
          </div>
        );
      },
    }),
    columnHelper.accessor('type', {
      header: 'Tip', meta: { filterType: 'select' },
      cell: (info) => typeBadge(info.getValue()),
    }),
    columnHelper.accessor('title', {
      header: 'Başlık', meta: { filterType: 'text' },
      cell: ({ row }) => {
        const title = pick(row.original, 'title', 'Title');
        const body = pick(row.original, 'body', 'Body');
        return (
          <div>
            <div style={{ fontWeight: 600 }}>{title || '-'}</div>
            {body && <div style={{ fontSize: 11.5, color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 320 }}>{body}</div>}
          </div>
        );
      },
    }),
    columnHelper.accessor('isRead', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => readBadge(info.getValue()),
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); setDetailRow(row.original); }}><ViewIcon /></button>
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('CreatedDate', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: pagedRows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(pick(row, 'id', 'Id')),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      if (filteredSorted.length === 0) { showToast('error', 'Dışa aktarılacak veri yok'); return; }
      const cols = [
        { headerName: 'Tarih', valueGetter: ({ row }) => formatDate(pick(row, 'createdDate', 'CreatedDate')) },
        { headerName: 'Kullanıcı', valueGetter: ({ row }) => pick(row, 'userName', 'UserName') || `#${pick(row, 'userId', 'UserId')}` },
        { headerName: 'E-posta', valueGetter: ({ row }) => pick(row, 'userEmail', 'UserEmail') || '' },
        { headerName: 'Tip', valueGetter: ({ row }) => typeLabel(pick(row, 'type', 'Type')) },
        { headerName: 'Başlık', valueGetter: ({ row }) => pick(row, 'title', 'Title') || '' },
        { headerName: 'İçerik', valueGetter: ({ row }) => pick(row, 'body', 'Body') || '' },
        { headerName: 'Durum', valueGetter: ({ row }) => (pick(row, 'isRead', 'IsRead') ? 'Okundu' : 'Yeni') },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      exportToExcel(filteredSorted, cols, `bildirimler_${dateStr}`);
      showToast('success', `${filteredSorted.length} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Kullanıcı / başlık ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={pagedRows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <>
      <ListPageShell
        title="Bildirimler"
        subtitle="Kullanıcılara gönderilen uygulama-içi bildirimlerin admin gözetim listesi. Okundu/okunmadı durumu ve deep-link payload'ı dahil."
        meta={`Toplam ${counts.total} • Okunmamış ${counts.unreadCount}`}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={(row) => setDetailRow(row)}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {detailRow && (
        <div style={s.overlay} onClick={() => setDetailRow(null)}>
          <div style={{ ...s.modal, maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>{pick(detailRow, 'title', 'Title') || 'Bildirim'}</h3>
              <button type="button" style={s.closeBtn} onClick={() => setDetailRow(null)}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Tip</label>
                  <div>{typeBadge(pick(detailRow, 'type', 'Type'))}</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Durum</label>
                  <div>{readBadge(pick(detailRow, 'isRead', 'IsRead'))}</div>
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Kullanıcı</label>
                  <div style={s.inputView}>
                    {pick(detailRow, 'userName', 'UserName') || `#${pick(detailRow, 'userId', 'UserId')}`}
                    {pick(detailRow, 'userEmail', 'UserEmail') ? ` (${pick(detailRow, 'userEmail', 'UserEmail')})` : ''}
                  </div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Gönderim</label>
                  <div style={s.inputView}>{formatDate(pick(detailRow, 'createdDate', 'CreatedDate'))}</div>
                </div>
              </div>
              {pick(detailRow, 'pointName', 'PointName') && (
                <div style={s.formGroup}>
                  <label style={s.label}>Nokta</label>
                  <div style={s.inputView}>{pick(detailRow, 'pointName', 'PointName')}</div>
                </div>
              )}
              <div style={s.formGroup}>
                <label style={s.label}>İçerik</label>
                <div style={s.textareaView}>{pick(detailRow, 'body', 'Body') || '-'}</div>
              </div>
              {pick(detailRow, 'readAt', 'ReadAt') && (
                <div style={s.formGroup}>
                  <label style={s.label}>Okunma Zamanı</label>
                  <div style={s.inputView}>{formatDate(pick(detailRow, 'readAt', 'ReadAt'))}</div>
                </div>
              )}
              {pick(detailRow, 'payloadJson', 'PayloadJson') && (
                <div style={s.formGroup}>
                  <label style={s.label}>Deep-link Payload</label>
                  <pre style={{
                    background: '#0f172a', color: '#e2e8f0', padding: 12, borderRadius: 8,
                    fontSize: 12, overflowX: 'auto', margin: 0,
                  }}>{pick(detailRow, 'payloadJson', 'PayloadJson')}</pre>
                </div>
              )}
            </div>
            <div style={s.modalFooter}>
              <button type="button" style={s.btnSecondary} onClick={() => setDetailRow(null)}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </>
  );
}

const TABS = [
  { key: 'monitor', label: 'İzleme' },
  { key: 'compose', label: 'Gönder' },
  { key: 'sent', label: 'Gönderilenler' },
];

export function NotificationsPage() {
  usePageMeta('Bildirimler', 'Mobil push bildirim gönderimi ve gözetimi');
  const [tab, setTab] = useState('monitor');
  // "Gönder"den sonra "Gönderilenler" listesini tazelemek için sayaç.
  const [sentReloadKey, setSentReloadKey] = useState(0);

  return (
    <div style={s.container}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 14, borderBottom: '1px solid var(--border-color, #e2e8f0)' }}>
        {TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)} style={{
            padding: '10px 18px', border: 'none', background: 'transparent', cursor: 'pointer',
            fontSize: 14, fontWeight: 600,
            color: tab === t.key ? 'var(--primary, #185fa5)' : '#64748b',
            borderBottom: tab === t.key ? '2px solid var(--primary, #185fa5)' : '2px solid transparent',
          }}>{t.label}</button>
        ))}
      </div>

      {tab === 'monitor' && <NotificationMonitorTab />}
      {tab === 'compose' && (
        <NotificationComposeTab onSent={() => { setSentReloadKey((k) => k + 1); setTab('sent'); }} />
      )}
      {tab === 'sent' && <NotificationSentTab reloadKey={sentReloadKey} />}
    </div>
  );
}

export default NotificationsPage;
